import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { errorToast, successToast } from "@telluscompany/design-system";
import { useLocalization } from "@i18n";
import { axiosDeleteData, axiosPatchData, axiosPostData } from "@shared/api/clients";

import type {
  CreateLicenseRequest,
  UpdateLicenseRequest,
} from "./licenses.types";

const LICENSES_QUERY_KEY = "licenses";

const getLicensesEndpoint = (tenantId: string) =>
  `v1/managerial/tenants/${tenantId}/licenses`;

export const createLicense = async (
  tenantId: string,
  payload: CreateLicenseRequest,
): Promise<string> => {
  return axiosPostData<string, CreateLicenseRequest>(
    getLicensesEndpoint(tenantId),
    payload,
  );
};

export const useCreateLicenseMutation = (tenantId: string) => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: (payload: CreateLicenseRequest) =>
      createLicense(tenantId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [LICENSES_QUERY_KEY, tenantId],
        exact: false,
      });
      successToast({
        description: translate("common.createSuccess"),
      });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorCreating"),
      });
    },
  });
};

export const updateLicense = async (
  tenantId: string,
  licenseId: string,
  payload: UpdateLicenseRequest,
): Promise<void> => {
  return axiosPatchData<void, UpdateLicenseRequest>(
    `${getLicensesEndpoint(tenantId)}/${licenseId}`,
    payload,
  );
};

export const useUpdateLicenseMutation = (
  tenantId: string,
  licenseId: string,
) => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: (payload: UpdateLicenseRequest) =>
      updateLicense(tenantId, licenseId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [LICENSES_QUERY_KEY, tenantId],
        exact: false,
      });
      successToast({
        description: translate("common.editSuccess"),
      });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorEditing"),
      });
    },
  });
};

export const deleteLicense = async (
  tenantId: string,
  licenseId: string,
): Promise<void> => {
  await axiosDeleteData<void>(
    `${getLicensesEndpoint(tenantId)}/${licenseId}`,
  );
};

export const useDeleteLicenseMutation = (tenantId: string) => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: (licenseId: string) =>
      deleteLicense(tenantId, licenseId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [LICENSES_QUERY_KEY, tenantId],
        exact: false,
      });
      successToast({
        description: translate("common.deleteSuccess"),
      });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorDeleting"),
      });
    },
  });
};
